/**
 * Die KA-Aufnahme eines Punktes zum ANZEIGEN aufbereitet: Blöcke, Zeilen, Texte.
 *
 * Alles kommt aus dem Feldkatalog (`ka-felder.ts`). Die Ansicht zählt deshalb kein
 * Feld selbst auf; ein neues Feld im Katalog erscheint hier von allein.
 *
 * Ohne Angular, damit die Regeln (was steht wo, was wird verborgen) prüfbar sind.
 */

import { KA6_FELDER, KaBlock, KaFeld } from './ka-felder';
import { KaZustand, kaZustand } from './ka-modell';

export interface KaAnzeigeOptionen {
	/**
	 * Gastmodus: Bearbeiter und Aufnahmedatum NICHT zeigen (Datennutzer und nicht
	 * Angemeldete, Regel `personenangabenVerbergen`). Beide stehen auch im KA-Bogen
	 * (Felder 4 und 5), also muss die Ansicht sie genauso verbergen wie der
	 * SEA-Reiter "Allgemein".
	 */
	personenVerbergen: boolean;
	/** Platzhalter an der Stelle eines verborgenen Wertes. */
	verborgenText: string;
	/** Macht aus dem gespeicherten Zeitpunkt ein lesbares Datum. */
	datumText?: (iso: string) => string;
}

export interface KaAnzeigeZeile {
	id: string;
	nr?: string;
	name: string;
	wert: string;
	zustand: KaZustand;
}

export interface KaAnzeigeBlock {
	block: KaBlock;
	titel: string;
	zeilen: KaAnzeigeZeile[];
}

export interface KaHorizontAnzeige {
	/** Laufende Nummer, wie im Bogen. */
	nr: number;
	/** Symbol und Grenzen, z. B. "Ah, 0 bis 12". */
	kopf: string;
	/** Nur die ausgefüllten Felder; Symbol und Grenzen stehen schon im Kopf. */
	zeilen: KaAnzeigeZeile[];
}

/** Die Felder, die Personenangaben tragen (`KaFeld.id`). */
export const KA_PERSONENFELDER: readonly string[] = ['datum', 'bearbeiter'];

const BLOCK_TITEL: Record<KaBlock, string> = {
	titel: 'Titeldaten',
	situation: 'Aufnahmesituation',
	horizont1: 'Horizontbezogene Daten I',
	horizont2: 'Horizontbezogene Daten II',
	profil: 'Profilkennzeichnung',
};

/** Liest einen Pfad wie "ka.horizonte[].humusgehalt"; `[]` steht für die Zeile `zeile`. */
export function leseKaWert(punkt: unknown, pfad: string, zeile?: number): unknown {
	let ort: unknown = punkt;
	for (const teil of pfad.split('.')) {
		if (ort === null || ort === undefined || typeof ort !== 'object') return undefined;
		const jeZeile = teil.endsWith('[]');
		ort = (ort as Record<string, unknown>)[jeZeile ? teil.slice(0, -2) : teil];
		if (jeZeile) {
			if (!Array.isArray(ort) || zeile === undefined) return undefined;
			ort = ort[zeile];
		}
	}
	return ort;
}

/** Ein einzelner Eintrag als Text. "nv" und "nb" stehen im Klartext da, so wie der Bogen sie erklärt. */
function eintragText(wert: unknown): string {
	const z = kaZustand(wert);
	if (z === 'leer') return '';
	if (z === 'nv') return 'nicht vorhanden';
	if (z === 'nb') return 'nicht bestimmt';
	return String(wert).trim();
}

/** Ein Feldwert als Text; mehrere Einträge durch Semikolon getrennt. */
export function kaWertText(wert: unknown): string {
	if (Array.isArray(wert)) return wert.map(eintragText).filter(t => t !== '').join('; ');
	return eintragText(wert);
}

/** Ober- und Untergrenze in einem Text: "0 bis 12". Fehlt eine, steht nur die andere da. */
function grenzenText(oben: unknown, unten: unknown): string {
	const o = kaWertText(oben);
	const u = kaWertText(unten);
	if (o !== '' && u !== '') return `${o} bis ${u}`;
	return o !== '' ? `ab ${o}` : u !== '' ? `bis ${u}` : '';
}

function zeileFuer(punkt: unknown, feld: KaFeld, optionen: KaAnzeigeOptionen, zeile?: number): KaAnzeigeZeile {
	const werte = feld.pfade.map(p => leseKaWert(punkt, p, zeile));
	let wert = feld.id === 'grenzen' ? grenzenText(werte[0], werte[1]) : kaWertText(werte.length === 1 ? werte[0] : werte);

	if (feld.art === 'datum' && wert !== '' && optionen.datumText) wert = optionen.datumText(wert);
	if (KA_PERSONENFELDER.includes(feld.id) && optionen.personenVerbergen) wert = optionen.verborgenText;

	return { id: feld.id, nr: feld.nr, name: feld.name, wert, zustand: kaZustand(werte.length === 1 ? werte[0] : werte) };
}

/** EIN Block des Bogens: ALLE seine Felder, auch die leeren. */
export function kaBlockAnzeige(punkt: unknown, block: KaBlock, optionen: KaAnzeigeOptionen): KaAnzeigeBlock {
	return {
		block,
		titel: BLOCK_TITEL[block],
		zeilen: KA6_FELDER.filter(f => f.block === block).map(f => zeileFuer(punkt, f, optionen)),
	};
}

/** Titeldaten, Aufnahmesituation und Profilkennzeichnung: ALLE Felder, auch die leeren. */
export function kaBloecke(punkt: unknown, optionen: KaAnzeigeOptionen): KaAnzeigeBlock[] {
	const bloecke: KaBlock[] = ['titel', 'situation', 'profil'];
	return bloecke.map(block => kaBlockAnzeige(punkt, block, optionen));
}

/** Die Horizonte: je Zeile ein Kopf und darunter nur, was ausgefüllt ist. */
export function kaHorizonte(punkt: unknown, optionen: KaAnzeigeOptionen): KaHorizontAnzeige[] {
	const zeilen = leseKaWert(punkt, 'ka.horizonte');
	if (!Array.isArray(zeilen)) return [];
	const felder = KA6_FELDER.filter(f => f.block === 'horizont1' || f.block === 'horizont2');

	return zeilen.map((_, i) => {
		const alle = felder.map(f => zeileFuer(punkt, f, optionen, i));
		const symbol = alle.find(z => z.id === 'horizontsymbol')?.wert ?? '';
		const grenzen = alle.find(z => z.id === 'grenzen')?.wert ?? '';
		return {
			nr: i + 1,
			kopf: [symbol !== '' ? symbol : 'ohne Symbol', grenzen].filter(t => t !== '').join(', '),
			zeilen: alle.filter(z => z.wert !== '' && z.id !== 'horizontsymbol' && z.id !== 'grenzen'),
		};
	});
}
